import { addDoc, collection, serverTimestamp, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const GLOBAL_LOG_COLLECTION = 'audit_logs';

/**
 * Log a generic change to an entity
 * @param {string} entityId - ID of the entity (e.g. reservationId)
 * @param {string} entityType - Type of entity (e.g. 'RESERVATION')
 * @param {string} actionType - Type of action (e.g. 'UPDATE', 'STATUS_CHANGE')
 * @param {Object} changes - Object containing old and new values { field: { old: 'a', new: 'b' } }
 * @param {Object} performedBy - User object { uid, name, role }
 * @param {string} context - Where the action was performed (e.g. 'ReservationDetail')
 * @param {string} tenantId - Optional tenant ID. If provided, logs to tenant subcollection.
 */
const sanitizeForFirestore = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(sanitizeForFirestore);
    if (typeof obj === 'object') {
        const sanitized = {};
        Object.keys(obj).forEach(key => {
            sanitized[key] = sanitizeForFirestore(obj[key]);
        });
        return sanitized;
    }
    return obj;
};

export const logAuditAction = async (entityId, entityType, actionType, changes, performedBy, context = '', tenantId = null) => {
    try {
        const collectionPath = tenantId ? `tenants/${tenantId}/audit_logs` : GLOBAL_LOG_COLLECTION;

        // Ensure no 'undefined' values reach Firestore
        const sanitizedChanges = sanitizeForFirestore(changes);

        const actor = typeof performedBy === 'object' ? performedBy : { uid: performedBy || 'system' };

        await addDoc(collection(db, collectionPath), {
            entityId,
            entityType,
            actionType,
            changes: sanitizedChanges,
            performedBy: {
                uid: actor.uid || actor.id || 'system',
                name: actor.fullName || actor.name || 'Sistem',
                role: actor.role || 'system'
            },
            context,
            tenantId,
            timestamp: serverTimestamp()
        });
    } catch (error) {
        console.error('Audit Log Error:', error);
    }
};

/**
 * Helper to log specific field updates
 */
export const logFieldUpdate = async (entityId, entityType, fieldName, oldValue, newValue, performedBy, context = '', tenantId = null) => {
    // Only log if values are different
    if (oldValue === newValue) return;

    await logAuditAction(
        entityId,
        entityType,
        'UPDATE_FIELD',
        {
            [fieldName]: { old: oldValue, new: newValue }
        },
        performedBy,
        context,
        tenantId
    );
};

/**
 * Helper to log status changes
 */
export const logStatusChange = async (entityId, entityType, oldStatus, newStatus, performedBy, context = '', tenantId = null) => {
    if (oldStatus === newStatus) return;

    await logAuditAction(
        entityId,
        entityType,
        'STATUS_CHANGE',
        {
            status: { old: oldStatus, new: newStatus }
        },
        performedBy,
        context,
        tenantId
    );
};

/**
 * Fetch audit logs for an entity
 */
export const getAuditLogs = async (entityId, tenantId = null) => {
    try {
        if (!entityId) return [];
        const collectionPath = tenantId ? `tenants/${tenantId}/audit_logs` : GLOBAL_LOG_COLLECTION;

        let q;
        try {
            q = query(
                collection(db, collectionPath),
                where('entityId', '==', entityId),
                orderBy('timestamp', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate() || new Date()
            }));
        } catch (indexError) {
            console.warn('Composite index might be missing, falling back to simple query:', indexError);
            // Fallback: Query only by entityId and sort in memory
            q = query(
                collection(db, collectionPath),
                where('entityId', '==', entityId)
            );
            const snapshot = await getDocs(q);
            const result = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate() || new Date()
            }));
            return result.sort((a, b) => b.timestamp - a.timestamp);
        }
    } catch (error) {
        console.error('Fetch Audit Log Error:', error);
        return [];
    }
};
